/**
 * Human record numbers (PO-130, QT-045) for records whose id is a uuid.
 *
 * The planning API serves `GET /planning/:project_id/record-refs`, a map of
 * `{ refs: { <entityKind>: { <record uuid>: <number> } } }`. The entity kinds
 * are the keys of ENTITY_PREFIX. This module keeps that map in a small
 * external store so any label can read it and any component using
 * `useRecordRefs()` re-renders when it arrives.
 *
 * The numbers are only a nicer label. URLs keep carrying the full uuid, and
 * when the map is empty or the call fails everything reads exactly as it did
 * before (a shortened uuid), so every failure here is swallowed.
 */
import { useSyncExternalStore } from 'react';
import { apiGet } from '../services/PlanningApiClient';

/** A project's numbers are re-fetched at most this often. */
export const RECORD_REFS_FRESH_MS = 60_000;

type RefMap = ReadonlyMap<string, ReadonlyMap<string, number>>;

interface RecordRefsResponse {
  refs?: Record<string, Record<string, unknown>> | null;
}

const EMPTY: RefMap = new Map();

let refs: RefMap = EMPTY;
let version = 0;
let loadedProjectId: string | null = null;
let lastAttemptAt = 0;
let inflight: { projectId: string; promise: Promise<void> } | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  version += 1;
  listeners.forEach(listener => listener());
}

/** Only whole numbers above zero are record numbers; anything else is ignored. */
function parseRefs(body: unknown): RefMap {
  const raw = (body as RecordRefsResponse | null | undefined)?.refs;
  if (!raw || typeof raw !== 'object') return EMPTY;
  const next = new Map<string, ReadonlyMap<string, number>>();
  for (const [kind, byId] of Object.entries(raw)) {
    if (!byId || typeof byId !== 'object') continue;
    const entries = new Map<string, number>();
    for (const [id, n] of Object.entries(byId)) {
      if (typeof n === 'number' && Number.isInteger(n) && n > 0) entries.set(id, n);
    }
    if (entries.size > 0) next.set(kind, entries);
  }
  return next;
}

function sameRefs(a: RefMap, b: RefMap): boolean {
  if (a.size !== b.size) return false;
  for (const [kind, byId] of a) {
    const other = b.get(kind);
    if (!other || other.size !== byId.size) return false;
    for (const [id, n] of byId) if (other.get(id) !== n) return false;
  }
  return true;
}

/** Forget every number (sign-out, or a different project became active). */
export function clearRecordRefs(): void {
  const hadRefs = refs.size > 0;
  refs = EMPTY;
  loadedProjectId = null;
  lastAttemptAt = 0;
  inflight = null;
  if (hadRefs) emit();
}

/**
 * Fetch the active project's numbers. Safe to call as often as you like:
 * one request at a time, at most one per minute per project, and a failure
 * (the endpoint not deployed yet, offline) leaves the current map alone.
 */
export function loadRecordRefs(projectId: string | null | undefined): Promise<void> {
  if (!projectId) {
    clearRecordRefs();
    return Promise.resolve();
  }
  if (loadedProjectId !== null && loadedProjectId !== projectId) clearRecordRefs();
  if (inflight && inflight.projectId === projectId) return inflight.promise;
  if (loadedProjectId === projectId && Date.now() - lastAttemptAt < RECORD_REFS_FRESH_MS) {
    return Promise.resolve();
  }

  loadedProjectId = projectId;
  lastAttemptAt = Date.now();
  const promise = apiGet<RecordRefsResponse>(`/planning/${projectId}/record-refs`)
    .then(body => {
      // The project changed while this was in flight: the answer is stale.
      if (loadedProjectId !== projectId) return;
      const next = parseRefs(body);
      if (sameRefs(refs, next)) return;
      refs = next;
      emit();
    })
    .catch(() => { /* keep whatever we had; labels fall back to the uuid */ })
    .finally(() => {
      if (inflight?.promise === promise) inflight = null;
    });
  inflight = { projectId, promise };
  return promise;
}

/** The record's number, or undefined when the API has not given it one. */
export function getRecordRef(kind: string, id: string | number | null | undefined): number | undefined {
  if (id === null || id === undefined || id === '') return undefined;
  return refs.get(kind)?.get(String(id));
}

/** The record carrying this number in the loaded project, or undefined (not loaded, or no such number). */
export function findRecordIdByNumber(kind: string, n: number): string | undefined {
  const byId = refs.get(kind);
  if (!byId) return undefined;
  for (const [id, num] of byId) if (num === n) return id;
  return undefined;
}

/**
 * The single numbered record whose uuid starts with these hex characters, or undefined when none or more than one does
 * (the caller then asks the API, which answers a clash with a list).
 */
export function findRecordIdByHash(kind: string, hex: string): string | undefined {
  const byId = refs.get(kind);
  if (!byId) return undefined;
  const want = hex.toLowerCase();
  let hit: string | undefined;
  for (const id of byId.keys()) {
    if (!id.toLowerCase().startsWith(want)) continue;
    if (hit !== undefined) return undefined;
    hit = id;
  }
  return hit;
}

/** The project the numbers in the store belong to, or null before the first load. */
export function recordRefsProjectId(): string | null {
  return loadedProjectId;
}

/** Calls `listener` whenever the numbers change (loaded, replaced, cleared). Returns the unsubscribe function. */
export function subscribeRecordRefs(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getVersion(): number {
  return version;
}

/**
 * Subscribe a component to the store. The returned version changes whenever
 * the map does; read numbers with getRecordRef / recordLabel during render.
 */
export function useRecordRefs(): { version: number; getRecordRef: typeof getRecordRef } {
  const current = useSyncExternalStore(subscribe, getVersion, getVersion);
  return { version: current, getRecordRef };
}

/** Test seam: back to a fresh, empty store. */
export function resetRecordRefsForTests(): void {
  refs = EMPTY;
  loadedProjectId = null;
  lastAttemptAt = 0;
  inflight = null;
  listeners.clear();
  version = 0;
}
